import { UseAxiosResult } from 'axios-hooks';
import { ProviderName } from '@shared/enums/provider-name.enum';
import { ISearchClientsRecord } from '@shared/models';
import { NotificationType } from '../../../../contexts/NotificationContext';
import { IClientLocalSystemNumberRecord, IClientLocalSystemNumbersResponse } from '../../../../hooks/useClientLocalSystemNumbers';
import { deactivateSystemNumbers, purchaseSystemNumbers } from '../../../Numbers/types';
import { IBulkDeactivateReviewSummary } from './DeactivateNumbersBulkModal';
import { IPurchaseReviewSummary, IPurchaseProviderResult } from './PurchaseNumbersModal';
import { normalizePhoneDigits } from './number-management.helpers';

type AddNotification = (notification: { header: string; content?: string; type?: NotificationType }) => void;
type RefetchFn = UseAxiosResult<IClientLocalSystemNumbersResponse>[1];

export interface IPurchaseHandlerContext {
  client: ISearchClientsRecord | undefined;
  addNotification: AddNotification;
  setIsPurchaseSubmitting: (isSubmitting: boolean) => void;
  setIsPostPurchase: (isPostPurchase: boolean) => void;
  setPurchaseReviewSummary: (summary: IPurchaseReviewSummary | null) => void;
  setShowPurchaseModal: (isVisible: boolean) => void;
  refetch: RefetchFn;
}

export interface IBulkDeactivateHandlerContext {
  client: ISearchClientsRecord | undefined;
  reviewSummary: IBulkDeactivateReviewSummary | null;
  addNotification: AddNotification;
  setIsBulkSubmitting: (isSubmitting: boolean) => void;
  closeBulkModals: () => void;
  refetch: RefetchFn;
}

export interface IDeactivateHandlerContext {
  client: ISearchClientsRecord | undefined;
  addNotification: AddNotification;
  setDeactivatingIds: (updater: (prev: string[]) => string[]) => void;
  refetch: RefetchFn;
}

// Returns a readable error message from a rejected Promise, falling back to a provided default.
const extractErrorMessage = (reason: unknown, fallback: string): string => {
  const err = reason as { response?: { data?: { message?: string } }; message?: string } | undefined;
  return err?.response?.data?.message ?? err?.message ?? fallback;
};

// Returns the set of indices where Promise.allSettled outcomes were rejected.
const getFailedIndices = (outcomes: PromiseSettledResult<unknown>[]): Set<number> => {
  return new Set(
    outcomes
      .map((outcome, index) => (outcome.status === 'rejected' ? index : -1))
      .filter(index => index !== -1)
  );
};

const SINCH_PROVIDERS = new Set<string>([ProviderName.SINCH_10DLC, ProviderName.SINCH_SHORT_CODE]);

// Annotates each result in the original summary with the error message from its failed purchase attempt.
const annotatePurchaseFailures = (
  summaryResults: IPurchaseProviderResult[],
  successfulResults: IPurchaseProviderResult[],
  outcomes: PromiseSettledResult<unknown>[],
  failedIndices: Set<number>,
): IPurchaseProviderResult[] => {
  return summaryResults.map(result => {
    const successIndex = successfulResults.findIndex(successful => successful.tcrCampaignId === result.tcrCampaignId);
    if (successIndex === -1 || !failedIndices.has(successIndex)) return result;

    const rejection = outcomes[successIndex] as PromiseRejectedResult;
    const message = extractErrorMessage(
      rejection.reason,
      `An unexpected error occurred while purchasing numbers for ${result.tcrCampaignId}. Please try again.`,
    );
    return { ...result, purchaseError: message };
  });
};

// Notifies the user of the outcome when one or more campaign purchases failed.
const notifyPartialOrFullPurchaseFailure = (
  succeededCount: number,
  addNotification: AddNotification,
): void => {
  if (succeededCount > 0) {
    addNotification({
      header: 'Partial purchase completed',
      content: `${succeededCount} campaign(s) purchased successfully. See review for failures.`,
      type: NotificationType.FAILURE,
    });
  } else {
    addNotification({
      header: 'Purchase failed',
      content: 'All campaigns failed to purchase. See review for details.',
      type: NotificationType.FAILURE,
    });
  }
};

// Notifies the user of a fully successful purchase, varying the message for Sinch providers
// whose orders are fulfilled asynchronously via email rather than immediately.
const notifyPurchaseSuccess = (
  successfulResults: IPurchaseProviderResult[],
  addNotification: AddNotification,
): void => {
  const hasSinch = successfulResults.some(result => SINCH_PROVIDERS.has(result.providerName));

  if (hasSinch) {
    addNotification({
      header: 'Order submitted',
      content: 'Your order has been submitted. You will receive an email once fulfilled.',
    });
  } else {
    addNotification({ header: 'Numbers purchased successfully!' });
  }
};

export const handlePurchaseConfirm = async (
  summary: IPurchaseReviewSummary,
  context: IPurchaseHandlerContext,
): Promise<void> => {
  if (!context.client?.environment) return;

  const successfulResults = summary.results.filter(result => result.phoneNumbers.length > 0);
  if (successfulResults.length === 0) return;

  try {
    context.setIsPurchaseSubmitting(true);

    const purchaseOutcomes = await Promise.allSettled(
      successfulResults.map(result => {
        if (!result.grTcrCampaignId) {
          return Promise.reject(new Error(`Missing GR campaign ID for ${result.tcrCampaignId}. Please try again.`));
        }
        return purchaseSystemNumbers(
          { id: result.grTcrCampaignId, environment: context.client!.environment },
          result.phoneNumbers,
        );
      })
    );

    const failedIndices = getFailedIndices(purchaseOutcomes);

    if (failedIndices.size > 0) {
      const annotatedResults = annotatePurchaseFailures(summary.results, successfulResults, purchaseOutcomes, failedIndices);
      const succeededCount = successfulResults.length - failedIndices.size;

      context.setIsPostPurchase(true);
      context.setPurchaseReviewSummary({ ...summary, results: annotatedResults });
      context.setShowPurchaseModal(false);
      notifyPartialOrFullPurchaseFailure(succeededCount, context.addNotification);
      await context.refetch();
      return;
    }

    context.setIsPostPurchase(true);
    context.setPurchaseReviewSummary(summary);
    context.setShowPurchaseModal(false);
    notifyPurchaseSuccess(successfulResults, context.addNotification);
    await context.refetch();
  } catch (error) {
    console.error(error);
    context.addNotification({
      header: 'Purchase error',
      content: 'An unexpected error occurred. Please check the grid and retry if needed.',
      type: NotificationType.FAILURE,
    });
    await context.refetch();
  } finally {
    context.setIsPurchaseSubmitting(false);
  }
};

export const handleBulkDeactivateConfirm = async (context: IBulkDeactivateHandlerContext): Promise<void> => {
  const { reviewSummary, client } = context;

  if (!reviewSummary || !client?.environment || reviewSummary.eligibleCampaigns.length === 0) {
    return;
  }

  const campaignsToDeactivate = reviewSummary.eligibleCampaigns
    .map(campaign => ({
      grTcrCampaignId: campaign.grTcrCampaignId,
      tcrCampaignLabel: campaign.tcrCampaignLabel,
      phoneNumbers: campaign.phoneNumbers
        .filter(phone => phone.exclusionReason === null)
        .map(phone => phone.phoneNumber),
    }))
    .filter(campaign => campaign.phoneNumbers.length > 0);

  if (campaignsToDeactivate.length === 0) {
    context.addNotification({
      header: 'No numbers to deactivate',
      content: 'All numbers in the review were excluded by the active filters.',
      type: NotificationType.INFO,
    });
    context.closeBulkModals();
    return;
  }

  try {
    context.setIsBulkSubmitting(true);

    const deactivateOutcomes = await Promise.allSettled(
      campaignsToDeactivate.map(campaign =>
        deactivateSystemNumbers({
          environment: client.environment!,
          tcrCampaignId: campaign.grTcrCampaignId,
          phoneNumbers: campaign.phoneNumbers,
        })
      )
    );

    const failedLabels = deactivateOutcomes
      .map((outcome, index) => (outcome.status === 'rejected' ? campaignsToDeactivate[index].tcrCampaignLabel : null))
      .filter((label): label is string => !!label);

    if (failedLabels.length === 0) {
      context.addNotification({ header: 'Numbers deactivated successfully!' });
    } else if (failedLabels.length < campaignsToDeactivate.length) {
      context.addNotification({
        header: 'Partial deactivation',
        content: `Some campaigns failed to deactivate: ${failedLabels.join(', ')}`,
        type: NotificationType.FAILURE,
      });
    } else {
      context.addNotification({
        header: 'Error',
        content: `Deactivation failed for all selected campaigns: ${failedLabels.join(', ')}`,
        type: NotificationType.FAILURE,
      });
    }
  } catch (error) {
    console.error(error);
    context.addNotification({
      header: 'Error',
      content: 'An unexpected error occurred when attempting to deactivate numbers.',
      type: NotificationType.FAILURE,
    });
  } finally {
    context.setIsBulkSubmitting(false);
    context.closeBulkModals();
    await context.refetch();
  }
};

export const handleDeactivate = async (
  item: IClientLocalSystemNumberRecord,
  context: IDeactivateHandlerContext,
): Promise<void> => {
  if (!item?.grTcrCampaignId || !context.client?.environment || !item?.phoneNumber) {
    context.addNotification({
      header: 'Error',
      content: 'This number cannot be deactivated because it is not associated with a TCR campaign.',
      type: NotificationType.FAILURE,
    });
    return;
  }

  try {
    context.setDeactivatingIds(prev => [...prev, item.id]);

    await deactivateSystemNumbers({
      environment: context.client.environment!,
      tcrCampaignId: item.grTcrCampaignId,
      phoneNumbers: [normalizePhoneDigits(item.phoneNumber)],
    });

    context.addNotification({ header: 'System Number deactivated successfully!' });
    await context.refetch();
  } catch (error) {
    console.error(error);
    context.addNotification({
      header: 'Error',
      content: 'An unexpected error occurred when attempting to deactivate the System Number.',
      type: NotificationType.FAILURE,
    });
  } finally {
    context.setDeactivatingIds(prev => prev.filter(id => id !== item.id));
  }
};
